import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import { useInView } from 'react-intersection-observer';
import type { HDEHighlightCard } from '~/modules/hde/types';
import { ScrollReveal } from './components/ScrollReveal';

const FALLBACK_TITLE = 'The Exhibitor Edit';

interface ExhibitorHighlightsProps {
    cards: HDEHighlightCard[];
    /** CMS DisplayTitle field used as section heading. Defaults to "The Exhibitor Edit" when absent. */
    displayTitle?: string | null;
    isMobile?: boolean;
}

function isExternalUrl(url: string): boolean {
    return /^(?:https?:)?\/\//.test(url);
}

function HighlightCard({
    card,
    isMobile,
}: {
    card: HDEHighlightCard;
    isMobile: boolean;
}) {
    const imageUrl = isMobile
        ? (card.backgroundMobileUrl ?? card.backgroundDesktopUrl)
        : (card.backgroundDesktopUrl ?? card.backgroundMobileUrl);
    const content = (
        <>
            <div
                className={cn(
                    'relative w-full overflow-hidden bg-[#e9e8e7]',
                    isMobile
                        ? 'aspect-[165/240] rounded-[0.25rem]'
                        : 'aspect-[440/666] rounded-lg',
                )}
            >
                {imageUrl && (
                    <Image
                        src={imageUrl}
                        alt={card.title}
                        fill={true}
                        fit="cover"
                        className="transition-transform duration-300 group-hover:scale-105"
                    />
                )}
            </div>
            <div className="flex flex-col">
                {card.professionalName && (
                    <p
                        className={cn(
                            'line-clamp-1 uppercase text-[#232323]',
                            isMobile
                                ? 'mt-[0.5rem] text-[0.40625rem] font-medium leading-[1] tracking-[0.08em]'
                                : 'mt-[2.625rem] text-[0.6875rem] font-medium leading-[1] tracking-[0.5px]',
                        )}
                    >
                        {card.professionalName}
                    </p>
                )}
                <h3
                    className={cn(
                        'overflow-hidden break-words font-ghost font-normal text-[#232323]',
                        isMobile
                            ? 'mt-[0.7rem] line-clamp-2 text-[0.875rem] leading-[1.15] tracking-[0.01em]'
                            : cn(
                                  'line-clamp-2 min-h-[2.3em] text-6 leading-[1.15] tracking-[-0.703px]',
                                  card.professionalName
                                      ? 'mt-[1rem]'
                                      : 'mt-[2.625rem]',
                              ),
                    )}
                >
                    {card.title}
                </h3>
                {card.description && (
                    <p
                        className={cn(
                            'line-clamp-3 text-[#232323]',
                            isMobile
                                ? 'mt-[0.625rem] text-[0.625rem] leading-[1.2]'
                                : 'mt-[1rem] text-[1rem] leading-[1.125]',
                        )}
                    >
                        {card.description}
                    </p>
                )}
            </div>
        </>
    );

    if (!card.link) {
        return <div className="flex h-full flex-col">{content}</div>;
    }

    return (
        <OptionalLink
            to={card.link}
            isNonRemixRoute={isExternalUrl(card.link)}
            className="group flex h-full flex-col"
        >
            {content}
        </OptionalLink>
    );
}

export const ExhibitorHighlights = ({
    cards,
    displayTitle,
    isMobile = false,
}: ExhibitorHighlightsProps) => {
    const heading = displayTitle?.trim() || FALLBACK_TITLE;
    const { ref: sectionRef, inView } = useInView({
        triggerOnce: true,
        threshold: 0.2,
    });

    if (cards.length === 0) {
        return null;
    }

    if (isMobile) {
        return (
            <section
                ref={sectionRef}
                data-testid="exhibitor-highlights"
                className="relative w-full bg-white px-[1.5rem] pb-[1.5rem] pt-[1.5rem]"
            >
                <div className="pt-[1.2rem]">
                    <p className="font-sans text-[1.25rem] font-medium uppercase leading-[0.9] tracking-[-0.00875em] text-black">
                        {heading}
                    </p>
                </div>
                <div className="mt-[1.75rem] grid grid-cols-2 gap-x-3 gap-y-8 md:gap-3">
                    {cards.map((card, index) => (
                        <ScrollReveal
                            key={card.id}
                            animation="fade-up"
                            duration={500}
                            delay={index * 100}
                        >
                            <HighlightCard card={card} isMobile={true} />
                        </ScrollReveal>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section
            ref={sectionRef}
            data-testid="exhibitor-highlights"
            className="relative w-full bg-white pt-[10rem]"
        >
            <div className="mx-[3.125rem] pt-5">
                <div className="overflow-hidden">
                    <div
                        className={cn(
                            'h-px w-full origin-left bg-black/20',
                            inView && 'hde-line-draw',
                        )}
                    />
                </div>
                <ScrollReveal
                    animation="fade-in"
                    duration={400}
                    delay={100}
                    className="mt-5"
                >
                    <p className="font-sans text-5.5 font-medium text-black">
                        {heading}
                    </p>
                </ScrollReveal>
            </div>

            <div className="mt-11 grid grid-cols-4 gap-[1.25rem] px-[3.125rem]">
                {cards.map((card, index) => (
                    <ScrollReveal
                        key={card.id}
                        animation="fade-up"
                        duration={500}
                        delay={index * 100}
                        className="flex h-full"
                    >
                        <HighlightCard card={card} isMobile={false} />
                    </ScrollReveal>
                ))}
            </div>
        </section>
    );
};
